import React from 'react'
import { useNavigate } from 'react-router-dom'

export default function Privacy() {
  const navigate = useNavigate()

  const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
    <div style={{ marginBottom: '40px' }}>
      <h3 style={{
        fontFamily: 'var(--font-display)', fontSize: '18px',
        color: 'var(--navy)', marginBottom: '12px',
        paddingBottom: '8px',
        borderBottom: '1px solid var(--cream-dark)',
      }}>
        {title}
      </h3>
      <div style={{
        fontSize: '14px', color: 'var(--ink-soft)',
        lineHeight: 1.8,
      }}>
        {children}
      </div>
    </div>
  )

  const P = ({ children }: { children: React.ReactNode }) => (
    <p style={{ marginBottom: '12px' }}>{children}</p>
  )

  const Li = ({ children }: { children: React.ReactNode }) => (
    <li style={{ marginBottom: '8px', marginLeft: '20px' }}>{children}</li>
  )

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--cream)',
    }}>

      {/* Nav */}
      <nav style={{
        background: 'var(--white)',
        borderBottom: '1px solid var(--cream-dark)',
        padding: '0 40px',
        height: '64px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        position: 'sticky',
        top: 0,
        zIndex: 100,
        boxShadow: 'var(--shadow-sm)',
      }}>
        <div
          onClick={() => navigate('/')}
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: '22px', fontWeight: 700,
            color: 'var(--navy)', cursor: 'pointer',
          }}
        >
          Velu<span style={{ color: 'var(--gold)' }}>mio</span>
        </div>
        <button
          onClick={() => navigate(-1)}
          style={{
            background: 'transparent',
            border: '1px solid var(--warm-border)',
            borderRadius: 'var(--radius-full)',
            padding: '8px 20px',
            fontSize: '13px', color: 'var(--muted)',
            cursor: 'pointer', fontFamily: 'var(--font-body)',
          }}
        >
          ← Back
        </button>
      </nav>

      {/* Content */}
      <div style={{
        maxWidth: '760px',
        margin: '0 auto',
        padding: '60px 24px',
      }}>

        {/* Header */}
        <div style={{ marginBottom: '48px' }}>
          <h1 style={{
            fontFamily: 'var(--font-display)',
            fontSize: 'clamp(28px, 4vw, 40px)',
            color: 'var(--navy)', marginBottom: '12px',
          }}>
            Privacy Policy
          </h1>
          <p style={{ fontSize: '14px', color: 'var(--muted)' }}>
            Last updated: July 2026 · Olatunji Ventures Ltd
          </p>
          <div style={{
            marginTop: '20px',
            padding: '16px 20px',
            background: 'rgba(200,150,62,0.08)',
            border: '1px solid rgba(200,150,62,0.2)',
            borderRadius: 'var(--radius-md)',
            fontSize: '14px', color: 'var(--ink-soft)',
            lineHeight: 1.7,
          }}>
            <strong style={{ color: 'var(--navy)' }}>Summary:</strong> Velumio
            handles sensitive mental health data. We encrypt everything, never
            sell your data, and give you full control. Your journal entries are
            encrypted and only you can read them. We are registered with the
            Information Commissioner's Office (ICO).
          </div>
        </div>

        <Section title="1. Who We Are">
          <P>
            Velumio is operated by Olatunji Ventures Ltd, a company registered
            in England and Wales. We are registered with the Information
            Commissioner's Office (ICO) as a data controller.
          </P>
          <P>
            If you have any questions about this policy or how we handle your
            data, please contact us at: <strong>hello@velumio.co.uk</strong>
          </P>
        </Section>

        <Section title="2. What Data We Collect">
          <P>We collect the following categories of personal data:</P>
          <P><strong>Account data:</strong></P>
          <ul style={{ marginBottom: '12px' }}>
            <Li>Email address and display name</Li>
            <Li>Encrypted password (we never store plain text passwords)</Li>
            <Li>Account creation date and verification status</Li>
          </ul>
          <P><strong>Journal and wellbeing data (Special Category Data):</strong></P>
          <ul style={{ marginBottom: '12px' }}>
            <Li>Journal entries — encrypted using AES-256 before storage</Li>
            <Li>Voice recordings — transcribed and immediately deleted; only text is stored</Li>
            <Li>Mood scores and emotional patterns</Li>
            <Li>Wellbeing scores and burnout risk assessments</Li>
            <Li>Financial stress scores (self-reported, 1–5 scale)</Li>
            <Li>Crisis language detection events (anonymised, no content stored)</Li>
          </ul>
          <P><strong>Profile data:</strong></P>
          <ul style={{ marginBottom: '12px' }}>
            <Li>Age range, gender, work pattern, caring responsibilities</Li>
            <Li>Physical activity level and chronotype</Li>
            <Li>General health factors (yes/no only — no medical details)</Li>
            <Li>Motivators, relaxation methods, and hobbies</Li>
          </ul>
          <P><strong>Technical data:</strong></P>
          <ul style={{ marginBottom: '12px' }}>
            <Li>IP address (used for security rate limiting only)</Li>
            <Li>Browser type and device information</Li>
            <Li>Usage patterns and feature interactions</Li>
          </ul>
        </Section>

        <Section title="3. Special Category Data">
          <P>
            Mental health journal entries, wellbeing scores, and health-related
            profile information constitute <strong>special category data</strong> under
            UK GDPR Article 9. We process this data on the basis of your
            <strong> explicit consent</strong>, which you provide when creating
            your account and completing your profile.
          </P>
          <P>
            You may withdraw consent at any time by deleting your account.
            Withdrawal of consent does not affect the lawfulness of processing
            before withdrawal.
          </P>
        </Section>

        <Section title="4. How We Use Your Data">
          <P>We use your data for the following purposes:</P>
          <ul style={{ marginBottom: '12px' }}>
            <Li>Providing the Velumio service — journaling, scoring, and weekly letters</Li>
            <Li>Calculating your personalised wellbeing score and energy budget</Li>
            <Li>Detecting crisis language and providing appropriate signposting</Li>
            <Li>Generating your weekly personal letter using AI</Li>
            <Li>Providing anonymised aggregate data to your organisation (B2B users only — minimum 5 team members, no individual data ever visible)</Li>
            <Li>Improving the platform based on anonymised usage patterns</Li>
            <Li>Complying with our legal obligations</Li>
          </ul>
        </Section>

        <Section title="5. Third-Party AI Processing">
          <P>
            Velumio uses artificial intelligence to analyse your journal entries
            and generate personalised content. This involves sharing your
            decrypted journal content with the following third-party AI providers:
          </P>
          <ul style={{ marginBottom: '12px' }}>
            <Li>
              <strong>OpenAI</strong> (GPT-4o-mini and Whisper) — for journal
              analysis, sentiment detection, and voice transcription. OpenAI
              processes data in the United States under Standard Contractual
              Clauses. We have a Data Processing Agreement in place with OpenAI.
            </Li>
            <Li>
              <strong>Anthropic</strong> (Claude) — for generating your weekly
              personal letter. Anthropic processes data in the United States
              under Standard Contractual Clauses. We have a Data Processing
              Agreement in place with Anthropic.
            </Li>
          </ul>
          <P>
            These providers are bound by their own privacy policies and our
            Data Processing Agreements. They do not use your data to train
            their models without your separate consent.
          </P>
        </Section>

        <Section title="6. How We Protect Your Data">
          <ul style={{ marginBottom: '12px' }}>
            <Li>
              <strong>Encryption at rest:</strong> All journal entries are
              encrypted using AES-256 before being stored in our database.
              Your encryption key is separate from your password.
            </Li>
            <Li>
              <strong>Encryption in transit:</strong> All data is transmitted
              over HTTPS with TLS encryption.
            </Li>
            <Li>
              <strong>Password security:</strong> Passwords are hashed using
              bcrypt with a cost factor of 12. We never store plain text passwords.
            </Li>
            <Li>
              <strong>Rate limiting:</strong> Authentication endpoints are
              rate limited to prevent brute force attacks.
            </Li>
            <Li>
              <strong>Access controls:</strong> Journal content is only
              accessible to you. No Velumio employee can read your entries.
            </Li>
          </ul>
        </Section>

        <Section title="7. Data Retention">
          <P>We retain your data for the following periods:</P>
          <ul style={{ marginBottom: '12px' }}>
            <Li>Journal entries — 2 years from creation, or until account deletion</Li>
            <Li>Wellbeing scores — 3 years from creation, or until account deletion</Li>
            <Li>Activity data — 1 year from creation, or until account deletion</Li>
            <Li>Account data — until account deletion plus 30 days</Li>
            <Li>Voice recordings — immediately deleted after transcription</Li>
          </ul>
          <P>
            You can request immediate deletion of all your data at any time
            by deleting your account in Settings.
          </P>
        </Section>

        <Section title="8. Your Rights Under UK GDPR">
          <P>You have the following rights regarding your personal data:</P>
          <ul style={{ marginBottom: '12px' }}>
            <Li><strong>Right of access</strong> — request a copy of all data we hold about you</Li>
            <Li><strong>Right to rectification</strong> — correct inaccurate data</Li>
            <Li><strong>Right to erasure</strong> — delete your account and all associated data</Li>
            <Li><strong>Right to restrict processing</strong> — limit how we use your data</Li>
            <Li><strong>Right to data portability</strong> — export your data in a machine-readable format</Li>
            <Li><strong>Right to object</strong> — object to processing based on legitimate interests</Li>
            <Li><strong>Right to withdraw consent</strong> — withdraw consent for special category data at any time</Li>
          </ul>
          <P>
            To exercise any of these rights, contact us at{' '}
            <strong>hello@velumio.co.uk</strong>. We will respond within 30 days.
          </P>
        </Section>

        <Section title="9. Cookies">
          <P>
            Velumio uses minimal cookies necessary for the service to function.
            We do not use advertising cookies or track you across other websites.
            We do not use Google Analytics or similar third-party tracking tools.
          </P>
        </Section>

        <Section title="10. Children">
          <P>
            Velumio is not intended for children under the age of 16. We do not
            knowingly collect personal data from children. If you believe a child
            has provided us with personal data, please contact us immediately.
          </P>
        </Section>

        <Section title="11. Changes to This Policy">
          <P>
            We may update this privacy policy from time to time. We will notify
            you of significant changes by email and by displaying a notice in
            the app. Continued use of Velumio after changes constitutes
            acceptance of the updated policy.
          </P>
        </Section>

        <Section title="12. Complaints">
          <P>
            If you are unhappy with how we handle your data, you have the right
            to lodge a complaint with the Information Commissioner's Office (ICO):
          </P>
          <ul>
            <Li>Website: <strong>ico.org.uk</strong></Li>
            <Li>Phone: <strong>0303 123 1113</strong></Li>
            <Li>Post: Information Commissioner's Office, Wycliffe House,
            Water Lane, Wilmslow, Cheshire, SK9 5AF</Li>
          </ul>
        </Section>

        {/* Footer */}
        <div style={{
          marginTop: '60px', paddingTop: '24px',
          borderTop: '1px solid var(--cream-dark)',
          display: 'flex', justifyContent: 'space-between',
          flexWrap: 'wrap' as const, gap: '12px',
          fontSize: '13px', color: 'var(--muted)',
        }}>
          <span>Olatunji Ventures Ltd · velumio.co.uk</span>
          <span>Last updated July 2026</span>
        </div>

      </div>
    </div>
  )
}